import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Search, MessageSquare, CalendarCheck, Sparkles, LogOut, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";

export default function Dashboard() {
  const { user, loading, login, logout } = useAuth();
  const { saved, recentSearches } = useApp();
  const [enquiries, setEnquiries] = useState([]);
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    if (user) {
      api.myEnquiries().then(setEnquiries).catch(() => {});
      api.properties({ limit: 4, new_launch: true }).then(setRecommended).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-pbs-grey"><Loader2 className="animate-spin" /></div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl pbs-gradient text-white flex items-center justify-center"><ShieldCheck size={28} /></div>
        <h1 className="mt-5 text-2xl font-extrabold text-pbs-dark">Sign in to your dashboard</h1>
        <p className="mt-2 text-pbs-grey">Access your saved properties, enquiries, scheduled visits and personalised recommendations.</p>
        <button onClick={login} data-testid="dashboard-login-btn" className="mt-6 inline-flex items-center gap-2 pbs-gradient text-white font-bold px-6 py-3 rounded-xl">
          <Sparkles size={16} /> Continue with Google
        </button>
      </div>
    );
  }

  const visits = enquiries.filter((e) => e.lead_type === "visit");
  const contactable = enquiries.filter((e) => e.lead_type !== "visit");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-pbs-purple" referrerPolicy="no-referrer" />
            : <div className="w-14 h-14 rounded-full pbs-gradient text-white flex items-center justify-center font-extrabold text-xl">{(user.name || user.email)[0].toUpperCase()}</div>}
          <div>
            <h1 className="text-2xl font-extrabold text-pbs-dark flex items-center gap-2">{user.name || "Welcome"} {user.is_admin && <span className="text-[10px] bg-pbs-purple text-white px-2 py-0.5 rounded-full font-bold uppercase">Admin</span>}</h1>
            <p className="text-sm text-pbs-grey">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="dashboard-logout-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-pbs-coral border border-pbs-line rounded-lg px-4 py-2 hover:bg-pbs-coral/5"><LogOut size={15} /> Logout</button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Heart} label="Saved" value={saved.length} />
        <StatCard icon={Search} label="Recent Searches" value={recentSearches.length} />
        <StatCard icon={MessageSquare} label="Enquiries" value={contactable.length} />
        <StatCard icon={CalendarCheck} label="Scheduled Visits" value={visits.length} />
      </div>

      <Section title="Saved Properties" link="/saved">
        {saved.length === 0 ? <Empty text="No saved properties yet." cta="Browse properties" to="/properties" /> :
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{saved.slice(0, 4).map((p) => <PropertyCard key={p.slug} property={p} compact />)}</div>}
      </Section>

      <Section title="Your Enquiries & Visits">
        {enquiries.length === 0 ? <Empty text="You haven't made any enquiries yet." cta="Explore properties" to="/properties" /> :
          <div className="bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow divide-y divide-pbs-line">
            {enquiries.slice(0, 8).map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <div>
                  <p className="font-semibold text-pbs-dark">{e.property_name || e.property_interest || "General enquiry"}</p>
                  <p className="text-xs text-pbs-grey">{e.lead_type} · {new Date(e.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-pbs-purple/10 text-pbs-purple uppercase">{e.status}</span>
              </div>
            ))}
          </div>}
      </Section>

      {recentSearches.length > 0 && (
        <Section title="Recent Searches">
          <div className="flex flex-wrap gap-2">{recentSearches.map((s, i) => <Link key={i} to="/properties" className="text-sm bg-white border border-pbs-line px-3 py-1.5 rounded-full text-pbs-dark hover:border-pbs-purple">{s}</Link>)}</div>
        </Section>
      )}

      {recommended.length > 0 && (
        <Section title="Recommended for You">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{recommended.map((p) => <PropertyCard key={p.slug} property={p} compact />)}</div>
        </Section>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-pbs-line p-5 pbs-shadow">
    <Icon size={20} className="text-pbs-purple" />
    <p className="mt-2 text-3xl font-extrabold text-pbs-dark">{value}</p>
    <p className="text-sm text-pbs-grey">{label}</p>
  </div>
);
const Section = ({ title, link, children }) => (
  <section className="mt-10">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-bold text-pbs-dark">{title}</h2>
      {link && <Link to={link} className="text-sm font-semibold text-pbs-purple">View all</Link>}
    </div>
    {children}
  </section>
);
const Empty = ({ text, cta, to }) => (
  <div className="bg-white rounded-2xl border border-dashed border-pbs-line p-8 text-center">
    <p className="text-pbs-grey">{text}</p>
    <Link to={to} className="mt-3 inline-block text-pbs-purple font-bold">{cta} →</Link>
  </div>
);
